<template>
  <div class="d-flex justify-content-end align-items-end">
    <span :id="showingLabelId" class="me-1">{{ $t("common.showing") }}</span>

    <div class="bg-white mx-2 cs-dropdown-select dropdown">
      <button
        ref="perPageToggle"
        type="button"
        class="btn dropdown-toggle"
        :id="perPageToggleId"
        :aria-expanded="state.isVisible ? 'true' : 'false'"
        aria-haspopup="listbox"
        :aria-controls="perPageListboxId"
        :aria-labelledby="`${showingLabelId} ${perPageValueId}`"
        @click="state.isVisible = !state.isVisible"
      >
        <span :id="perPageValueId">{{ state.selectedOption.label }}</span>
      </button>
      <div
        :id="perPageListboxId"
        role="listbox"
        :class="{ 'd-block': state.isVisible }"
        class="dropdown-menu"
        :aria-labelledby="perPageToggleId"
      >
        <button
          v-for="(item, index) in options || defaultOptions"
          :key="index"
          type="button"
          role="option"
          class="dropdown-item text-start border-0 bg-transparent w-100"
          :aria-selected="
            item.value === state.selectedOption.value ? 'true' : 'false'
          "
          @click="onSelectOption(item)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>

    <div
      v-if="
        pagination.total > 0 &&
        state.selectedOption.label != $t('common.all_entries')
      "
    >
      <span class="d-inline-block me-2">{{ $t("common.entries") }}</span>
      {{ $t("common.showing") }} {{ pagination.from }}
      {{ $t("common.showing_to") }} {{ pagination.to }}
      {{ $t("common.showing_of") }} {{ pagination.total }}
    </div>
    <div
      v-if="
        pagination.total > 0 &&
        state.selectedOption.label == $t('common.all_entries')
      "
    >
      {{ $t("common.showing") }} 1 {{ $t("common.showing_to") }}
      {{ pagination.to }} {{ $t("common.showing_of") }} {{ pagination.total }}
    </div>
  </div>
</template>

<script>
import { reactive, computed, watch, ref, nextTick } from "vue"
import { useI18n } from "vue-i18n"

let perPageSelectorUid = 0

export default {
  name: "PerPageSelector",
  props: {
    pagination: {
      type: Object
    },
    options: {
      type: Array,
      deep: true,
      default: null
    },
    action: {
      type: Function
    }
  },
  setup(props) {
    const { t } = useI18n()
    const uid = `per-page-${++perPageSelectorUid}`
    const showingLabelId = `${uid}-showing-label`
    const perPageToggleId = `${uid}-toggle`
    const perPageValueId = `${uid}-value`
    const perPageListboxId = `${uid}-listbox`

    const defaultOptions = computed(() => [
      {
        label: "25",
        value: 25
      },
      {
        label: "50",
        value: 50
      },
      {
        label: "100",
        value: 100
      },
      {
        label: t("common.all_entries"),
        value: 75
      }
    ])

    const state = reactive({
      isVisible: false,
      selectedOption: {
        label: "25",
        value: 25
      }
    })

    const perPageToggle = ref(null)

    /* Return focus to the trigger when the menu closes. The selected option lives
       inside the menu that is about to be hidden; without this, focus falls back to
       <body> and screen readers re-announce the window/page title before our live
       region can announce "Loading". */
    const restoreFocusToToggle = () => {
      nextTick(() => {
        perPageToggle.value?.focus?.()
      })
    }

    const selecteActive = (item) => {
      state.selectedOption = item
      state.isVisible = false
      restoreFocusToToggle()
    }

    const onSelectOption = (item) => {
      if (typeof props.action === "function") {
        props.action(item)
      }
      selecteActive(item)
    }

    /** Supports `per_page` as `{ value, label }` or a primitive (e.g. 25). */
    const normalizePerPageSelection = (pp, optionsList) => {
      if (pp == null) return null
      if (
        typeof pp === "object" &&
        Object.prototype.hasOwnProperty.call(pp, "value")
      ) {
        const v = pp.value
        if (v === undefined || v === null) return null
        return {
          label: pp.label != null ? String(pp.label) : String(v),
          value: v
        }
      }
      const value =
        typeof pp === "number" ? pp : typeof pp === "string" ? Number(pp) : NaN
      if (Number.isNaN(value)) return null
      const match = optionsList?.find?.((o) => o.value === value)
      return {
        label: match != null ? String(match.label) : String(value),
        value
      }
    }

    watch(
      () => [props.pagination?.per_page, props.options, defaultOptions.value],
      () => {
        const optionsList = props.options || defaultOptions.value
        const next = normalizePerPageSelection(
          props.pagination?.per_page,
          optionsList
        )
        if (next == null) return
        state.selectedOption = next
      },
      { immediate: true }
    )

    return {
      state,
      perPageToggle,
      selecteActive,
      onSelectOption,
      defaultOptions,
      showingLabelId,
      perPageToggleId,
      perPageValueId,
      perPageListboxId
    }
  }
}
</script>
